import { useState, useMemo, useEffect } from 'react';
import api from '@/lib/api';
import { UserPlus, RotateCw, Trash2, Pencil, CheckCircle2, XCircle, ChevronRight, Shield, Mail, Phone, Search, Power, Check, X, AlertTriangle, Clock, MailCheck, Zap } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'ceo', label: 'CEO' },
  { value: 'leader', label: 'Division Leader' },
  { value: 'sub_leader', label: 'Sub-Leader' },
  { value: 'submitter', label: 'Submitter' },
  { value: 'cost_controller', label: 'Cost Controller' },
  { value: 'viewer', label: 'Viewer' },
];

// 11-May-2026 · Categories drive the User Management dropdown. Picking a
// category pre-fills a recommended scope set. CEO can still tick/untick
// individual scopes after.
const CATEGORY_OPTIONS = [
  { value: '', label: '— None —', scopes: [] },
  { value: 'hr', label: 'HR · People & Training', scopes: ['people', 'training', 'profile', 'submit_update'] },
  { value: 'operations', label: 'Operations · Outlets', scopes: ['outlets', 'profile', 'submit_update'] },
  { value: 'finance', label: 'Finance · Targets & Pace', scopes: ['finance', 'profile'] },
  { value: 'concierge_team', label: 'Concierge® Team · CI + Workbench', scopes: ['client_intel', 'workbench', 'profile', 'submit_update'] },
  { value: 'compliance', label: 'Compliance Officer', scopes: ['compliance', 'profile', 'submit_update'] },
  { value: 'custom', label: 'Custom (pick scopes manually)', scopes: null },
];

// 11-May-2026 · All scope tokens. Keep in sync with permissions.js +
// Layout.js NAV_PATH_SCOPE_MAP.
const SCOPE_OPTIONS = [
  { value: 'people', label: 'People · KYC docs' },
  { value: 'training', label: 'Training' },
  { value: 'profile', label: 'Own Profile' },
  { value: 'submit_update', label: 'Submit Update (WhatsApp)' },
  { value: 'compliance', label: 'Compliance docs' },
  { value: 'outlets', label: 'Outlet Intel' },
  { value: 'client_intel', label: 'Client Intelligence + Newsletter' },
  { value: 'workbench', label: 'Member Care Workbench' },
  { value: 'finance', label: 'Finance · Targets & Pace' },
  { value: 'mira', label: 'Operator Mira admin' },
  { value: 'dashboard', label: 'Group Dashboard' },
  { value: 'division', label: 'Division view' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', bg: '#DCFCE7', fg: '#166534' },
  { value: 'suspended', label: 'Suspended', bg: '#FEF3C7', fg: '#92400E' },
  { value: 'deactivated', label: 'Deactivated', bg: '#FEE2E2', fg: '#991B1B' },
];

function timeAgo(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso); if (isNaN(d)) return '—';
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s/86400)}d ago`;
  if (s < 86400 * 30) return `${Math.floor(s/(86400*7))}w ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function trainingBadge(t) {
  if (!t || !t.assigned) return { label: '—', bg: '#F3F4F6', fg: '#9CA3AF' };
  const pct = t.completion_pct ?? 0;
  if (pct >= 95) return { label: `${t.completed}/${t.assigned} ✓`, bg: '#DCFCE7', fg: '#166534' };
  if (t.overdue > 0) return { label: `${t.completed}/${t.assigned} · ${t.overdue} overdue`, bg: '#FEE2E2', fg: '#991B1B' };
  return { label: `${t.completed}/${t.assigned} (${pct}%)`, bg: '#FEF3C7', fg: '#92400E' };
}

function loginBadge(u) {
  if (!u.last_login_at) return { label: 'Never logged in', bg: '#FEE2E2', fg: '#991B1B' };
  const days = (Date.now() - new Date(u.last_login_at).getTime()) / 86400000;
  if (days < 2) return { label: timeAgo(u.last_login_at), bg: '#DCFCE7', fg: '#166534' };
  if (days < 7) return { label: timeAgo(u.last_login_at), bg: '#FEF3C7', fg: '#92400E' };
  return { label: timeAgo(u.last_login_at), bg: '#FEE2E2', fg: '#991B1B' };
}

/**
 * The admin control centre. Lists every user with adoption + engagement signals
 * (last login, training completion, activity counts) so the CEO can answer
 * "who's actually using ScaleBoard" at a glance.
 */
export default function UserAdminTable({ users, divisions, onRefresh, hrMode = false }) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null); // user object being edited
  const [creating, setCreating] = useState(false);
  const [trainingFor, setTrainingFor] = useState(null); // user object whose training to view
  const [selected, setSelected] = useState(new Set()); // user ids checked
  const [bulkBusy, setBulkBusy] = useState(false);

  const divOptions = useMemo(() => divisions.map(d => ({ id: d.id, name: d.name })), [divisions]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (roleFilter !== 'all' && u.role !== roleFilter) return false;
      if (statusFilter !== 'all' && (u.status || 'active') !== statusFilter) return false;
      if (q) {
        const hay = `${u.name} ${u.email} ${u.phone || ''} ${u.role}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  // Selectable subset (never allow bulk on CEO rows).
  const selectableIds = useMemo(() => filtered.filter(u => u.role !== 'ceo').map(u => u._id), [filtered]);
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) { setSelected(new Set()); return; }
    setSelected(new Set(selectableIds));
  };
  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);  };
  const clearSelection = () => setSelected(new Set());

  // One-click leader email migration (doctrine v1.7 · Dipali 22-Apr-2026).
  // Idempotent — running twice just returns `already_migrated` for each.
  const [migrating, setMigrating] = useState(false);
  const [migrateReport, setMigrateReport] = useState(null);
  const runEmailMigration = async () => {
    if (!window.confirm(
      "Migrate leader emails on THIS environment?\n\n" +
      "• srikanth@lesconcierges.in → srikanth.ds@lesconcierges.co.in\n" +
      "• aditya@lesconcierges.in → sikand.adi@gmail.com\n\n" +
      "Both passwords will be reset to Welcome@2026. This is idempotent — " +
      "safe to run multiple times.")) return;
    setMigrating(true); setMigrateReport(null);
    try {
      const res = await api.post('/admin/migrate-leader-emails');
      setMigrateReport(res.data.report || []);
      onRefresh && onRefresh();
    } catch (err) {
      alert('Migration failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setMigrating(false);
    }
  };

  // One-click post-deploy idempotent seed chain.
  // Fires: LCG academy + eZee placeholder + legacy flag + stage_key backfill
  //        + deal-date backfill + outlet-field backfill. Every step safe to rerun.
  const [seeding, setSeeding] = useState(false);
  const [seedReport, setSeedReport] = useState(null);
  const runAllSeeds = async () => {
    if (!window.confirm(
      "Run ALL idempotent seeds on THIS environment?\n\n" +
      "• LCG Academy (47 SOPs + 5,922 assignments + 126 people)\n" +
      "• eZee placeholder (6,730 orders + 482 reservations + 284 guests)\n" +
      "• Flag legacy migration deals\n" +
      "• Backfill stage_key on pipeline_deals\n" +
      "• Backfill created_at on legacy deals\n" +
      "• Backfill outlet field on reviews\n" +
      "• Parent-child architecture (CC + CS)\n\n" +
      "All idempotent — safe to rerun after every deploy.")) return;
    setSeeding(true); setSeedReport(null);
    try {
      const res = await api.post('/admin/seed/run-all');
      setSeedReport(res.data);
    } catch (err) {
      alert('Seed run failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setSeeding(false);
    }
  };

  // Mira QA — test all 33 intents, show per-test PASS/FAIL
  const [qaRunning, setQaRunning] = useState(false);
  const [qaReport, setQaReport] = useState(null);
  const runMiraQA = async () => {
    if (!window.confirm("Run Mira QA suite (33 test intents)?\n\nMira parses each command but does NOT commit anything to the DB. Auto-cleanup runs at the end. Takes ~3 minutes.")) return;
    setQaRunning(true); setQaReport(null);
    try {
      const start = await api.post('/admin/mira-qa');
      const runId = start.data?.run_id;
      if (!runId) throw new Error('No run_id returned');
      // Poll every 8s until completed/error
      let attempts = 0;
      while (attempts < 50) {
        await new Promise(r => setTimeout(r, 8000));
        try {
          const r = await api.get(`/admin/mira-qa/runs/${runId}`);
          if (r.data?.status === 'completed' || r.data?.status === 'error') {
            setQaReport(r.data);
            break;
          }
        } catch { /* transient — keep polling */ }
        attempts++;
      }
    } catch (err) {
      alert('QA run failed: ' + (err?.response?.data?.detail || err.message));
    } finally {
      setQaRunning(false);
    }
  };

  const runBulk = async (action, value, confirmMsg) => {
    if (selected.size === 0) return;
    const label = confirmMsg || `${action.replace('_', ' ')}`;
    if (!window.confirm(`Apply "${label}" to ${selected.size} user${selected.size === 1 ? '' : 's'}?`)) return;
    setBulkBusy(true);
    try {
      await api.post('/admin/users/bulk', {
        user_ids: Array.from(selected),
        action,
        value,
      });
      clearSelection();
      onRefresh();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Bulk action failed');
    }
    setBulkBusy(false);
  };

  const counts = useMemo(() => ({
    total: users.length,
    active: users.filter(u => (u.status || 'active') === 'active').length,
    neverLogged: users.filter(u => !u.last_login_at).length,
    inactive7d: users.filter(u => u.last_login_at && (Date.now() - new Date(u.last_login_at).getTime()) > 7 * 86400000).length,
  }), [users]);

  const handleResetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name} to Welcome@2026?`)) return;
    await api.post(`/admin/users/${u._id}/reset-password`);
    alert('Password reset to Welcome@2026. User must change on next login.');
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.name}? This cannot be undone.`)) return;
    await api.delete(`/admin/users/${u._id}`);
    onRefresh();
  };

  return (
    <div className="space-y-4" data-testid="user-admin-table">
      {/* Adoption strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <AdoptionPill label="Total users" value={counts.total} />
        <AdoptionPill label="Active" value={counts.active} accent="#166534" />
        <AdoptionPill label="Never logged in" value={counts.neverLogged} accent={counts.neverLogged > 0 ? '#991B1B' : '#9CA3AF'} warning={counts.neverLogged > 0} />
        <AdoptionPill label="Inactive 7+ days" value={counts.inactive7d} accent={counts.inactive7d > 0 ? '#92400E' : '#9CA3AF'} warning={counts.inactive7d > 0} />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#9CA3AF' }} />
          <input placeholder="Search name / email / phone…" value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="user-search" />
        </div>
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="user-role-filter">
          <option value="all">All roles</option>
          {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="user-status-filter">
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {!hrMode && <><button onClick={runEmailMigration} disabled={migrating}
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border disabled:opacity-50"
          style={{ borderColor: '#2A7B88', color: '#2A7B88' }}
          title="One-click: migrate Srikanth + Aditya to their new emails (idempotent)"
          data-testid="migrate-emails-btn">
          <MailCheck size={13} /> {migrating ? 'Migrating…' : 'Migrate Emails'}
        </button>
        <button onClick={runAllSeeds} disabled={seeding}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border disabled:opacity-50"
          style={{ borderColor: '#F59E0B', color: '#92400E', backgroundColor: '#FFFBEB' }}
          title="Post-deploy: run every idempotent seed + cleanup in one shot (LCG, eZee, stage_key, legacy flag, deal dates, outlet fields)"
          data-testid="run-all-seeds-btn">
          <Zap size={13} /> {seeding ? 'Seeding…' : 'Run All Seeds'}
        </button>
        <button onClick={runMiraQA} disabled={qaRunning}
          className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium border disabled:opacity-50"
          style={{ borderColor: '#2A7B88', color: '#0F4C5C', backgroundColor: '#F0FDFA' }}
          title="Run Mira's 33-intent quality gate. Each test parses a real command, asserts the right intent and division. Auto-cleanup at the end."
          data-testid="run-mira-qa-btn">
          <Zap size={13} /> {qaRunning ? 'Testing Mira…' : 'Test Mira'}
        </button></>}
        <button onClick={() => setCreating(true)} className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium text-white" style={{ backgroundColor: '#1B2A4A' }} data-testid="add-user-btn"><UserPlus size={13} /> Add User</button>
      </div>

      {/* Migration result banner */}
      {migrateReport && (
        <div className="rounded-lg border p-3 text-xs space-y-1" style={{ borderColor: '#2A7B88', backgroundColor: '#F0FDFA' }} data-testid="migrate-report">
          <div className="font-semibold" style={{ color: '#1B2A4A' }}>Leader email migration result:</div>
          {migrateReport.map((r, i) => (
            <div key={i} style={{ color: '#1F2937' }}>
              <span className="font-mono">{r.old}</span>
              {' → '}
              <span className="font-mono font-semibold">{r.new}</span>
              {' · '}
              <span style={{
                color: r.status === 'migrated' ? '#2D8B5E' :
                       r.status === 'already_migrated' ? '#6B7280' : '#B91C1C',
                fontWeight: 600,
              }}>{r.status}{r.users_modified != null ? ` (users:${r.users_modified}, people:${r.people_modified})` : ''}</span>
            </div>
          ))}
          <button onClick={() => setMigrateReport(null)} className="underline mt-1" style={{ color: '#6B7280', fontSize: 10 }}>Dismiss</button>
        </div>
      )}

      {/* Run-all seeds result banner */}
      {seedReport && (
        <div className="rounded-lg border p-3 text-xs space-y-1"
             style={{
               borderColor: seedReport.ok ? '#10B981' : '#DC2626',
               backgroundColor: seedReport.ok ? '#ECFDF5' : '#FEF2F2',
             }}
             data-testid="seed-report">
          <div className="flex items-center justify-between">
            <div className="font-semibold" style={{ color: '#1B2A4A' }}>
              {seedReport.ok ? '✅' : '⚠️'} Post-deploy seed run — {seedReport.summary} · {seedReport.total_elapsed_ms}ms
            </div>
            <button onClick={() => setSeedReport(null)} className="underline" style={{ color: '#6B7280', fontSize: 10 }}>Dismiss</button>
          </div>
          {(seedReport.steps || []).map((s, i) => (
            <div key={i} className="flex items-start gap-2" style={{ color: '#1F2937' }}>
              <span>{s.ok ? '✅' : '❌'}</span>
              <span className="font-mono w-40 flex-shrink-0">{s.step}</span>
              <span className="text-[11px]" style={{ color: '#6B7280' }}>
                {s.ok ? `${s.elapsed_ms}ms` : ''}
              </span>
              <span className="flex-1">
                {s.ok ? (
                  s.step === 'lcg_academy' && s.result?.restored
                    ? `restored: ${JSON.stringify(s.result.restored)}`
                    : s.step === 'ezee_placeholder'
                      ? `orders=${s.result?.orders} · reservations=${s.result?.reservations} · menu=${s.result?.menu_items}`
                      : s.step === 'backfill_stage_keys'
                        ? `backfilled=${s.result?.backfilled_count} · total=${s.result?.total_deals}/${s.result?.deals_with_stage_key}`
                        : `touched=${s.result?.touched ?? 0}`
                ) : (
                  <span style={{ color: '#B91C1C' }}>{s.error}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Mira QA result banner */}
      {qaReport && (
        <div className="rounded-lg border p-3 text-xs space-y-1"
             style={{
               borderColor: qaReport.summary?.fail === 0 && qaReport.summary?.error === 0 ? '#10B981' : '#F59E0B',
               backgroundColor: qaReport.summary?.fail === 0 && qaReport.summary?.error === 0 ? '#ECFDF5' : '#FFFBEB',
             }}
             data-testid="mira-qa-report">
          <div className="flex items-center justify-between">
            <div className="font-semibold" style={{ color: '#1B2A4A' }}>
              {qaReport.summary?.fail === 0 && qaReport.summary?.error === 0 ? '✅' : '⚠️'} Mira QA —
              {' '}PASS={qaReport.summary?.pass}{' · '}FAIL={qaReport.summary?.fail}{' · '}ERROR={qaReport.summary?.error}{' · '}{qaReport.elapsed_seconds}s
            </div>
            <button onClick={() => setQaReport(null)} className="underline" style={{ color: '#6B7280', fontSize: 10 }}>Dismiss</button>
          </div>
          {(qaReport.results || []).filter(r => r.status !== 'pass').map((r) => (
            <div key={r.id} className="flex items-start gap-2 font-mono" style={{ color: '#1F2937', fontSize: 10 }}>
              <span>{r.status === 'pass' ? '✅' : r.status === 'fail' ? '❌' : '⚠️'}</span>
              <span className="w-6 flex-shrink-0">#{r.id}</span>
              <span className="w-44 flex-shrink-0 truncate" title={r.label}>{r.label}</span>
              <span className="flex-1 truncate" style={{ color: '#6B7280' }} title={r.evidence}>{r.evidence}</span>
            </div>
          ))}
          {qaReport.summary?.pass === qaReport.summary?.total && (
            <div className="font-semibold mt-1" style={{ color: '#059669' }}>All {qaReport.summary.total} tests passed. Mira is healthy.</div>
          )}
          {qaReport.cleanup && Object.values(qaReport.cleanup).some(n => n > 0) && (
            <div className="text-[10px] italic" style={{ color: '#6B7280' }}>
              Cleanup: {Object.entries(qaReport.cleanup).filter(([_, n]) => n > 0).map(([k, n]) => `${k}=${n}`).join(' · ')}
            </div>
          )}
        </div>
      )}

      {/* Bulk Action Bar — appears only when selection > 0 */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border p-3 shadow-sm" style={{ borderColor: '#1B2A4A', backgroundColor: '#EEF2FF' }} data-testid="bulk-actions-bar">
          <span className="text-xs font-semibold" style={{ color: '#1B2A4A' }}>
            {selected.size} selected
          </span>
          <button onClick={clearSelection} className="text-[10px] underline" style={{ color: '#6B7280' }} data-testid="bulk-clear">Clear</button>
          <div className="mx-2 h-4 w-px" style={{ backgroundColor: '#D1D5DB' }} />

          <BulkDropdown label="Change role…" disabled={bulkBusy} testId="bulk-role">
            {['leader', 'sub_leader', 'submitter', 'cost_controller', 'viewer'].map(r => (
              <button key={r} type="button" onClick={() => runBulk('set_role', r)} className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs capitalize" data-testid={`bulk-role-${r}`}>
                {r.replace('_', ' ')}
              </button>
            ))}
          </BulkDropdown>

          <BulkDropdown label="Change division…" disabled={bulkBusy} testId="bulk-division">
            <button type="button" onClick={() => runBulk('set_division', '')} className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs italic" style={{ color: '#6B7280' }}>None</button>
            {divisions.map(d => (
              <button key={d.id} type="button" onClick={() => runBulk('set_division', d.id)} className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs">
                {d.name}
              </button>
            ))}
          </BulkDropdown>

          <button onClick={() => runBulk('set_status', 'active')} disabled={bulkBusy} className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border hover:shadow-sm disabled:opacity-50" style={{ borderColor: '#2D8B5E', color: '#166534', backgroundColor: '#fff' }} data-testid="bulk-activate">
            <Check size={11} /> Activate
          </button>
          <button onClick={() => runBulk('set_status', 'suspended')} disabled={bulkBusy} className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border hover:shadow-sm disabled:opacity-50" style={{ borderColor: '#BA7517', color: '#92400E', backgroundColor: '#fff' }} data-testid="bulk-suspend">
            <Power size={11} /> Suspend
          </button>
          <button onClick={() => runBulk('set_status', 'deactivated')} disabled={bulkBusy} className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border hover:shadow-sm disabled:opacity-50" style={{ borderColor: '#991B1B', color: '#991B1B', backgroundColor: '#fff' }} data-testid="bulk-deactivate">
            <XCircle size={11} /> Deactivate
          </button>
          <button onClick={() => runBulk('reset_password', null)} disabled={bulkBusy} className="flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-md border hover:shadow-sm disabled:opacity-50" style={{ borderColor: '#E5E1DC', color: '#6B7280', backgroundColor: '#fff' }} data-testid="bulk-reset-password">
            <RotateCw size={11} /> Reset Password
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
              <th className="px-3 py-2.5 w-8">
                <input type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected; }}
                  onChange={toggleAll}
                  className="rounded cursor-pointer"
                  data-testid="select-all-users"
                  title="Select all (excluding CEO)" />
              </th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Name</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden md:table-cell" style={{ color: '#6B7280' }}>Contact</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Role</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden lg:table-cell" style={{ color: '#6B7280' }}>Division(s)</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Last Login</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Training</th>
              <th className="text-left px-3 py-2.5 text-xs font-medium hidden lg:table-cell" style={{ color: '#6B7280' }}>Activity</th>
              <th className="text-right px-3 py-2.5 text-xs font-medium" style={{ color: '#6B7280' }}>Actions</th>
            </tr></thead>
            <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
              {filtered.map(u => {
                const statusOpt = STATUS_OPTIONS.find(s => s.value === (u.status || 'active')) || STATUS_OPTIONS[0];
                const tr = trainingBadge(u.training);
                const lg = loginBadge(u);
                const primaryDiv = divisions.find(d => d.id === u.division_id);
                const addDivs = (u.additional_division_ids || []).map(id => divisions.find(d => d.id === id)?.name).filter(Boolean);
                const act = u.activity || {};
                return (
                  <tr key={u._id} className={`hover:bg-gray-50 ${selected.has(u._id) ? 'bg-indigo-50' : ''}`} data-testid={`user-row-${u._id}`}>
                    <td className="px-3 py-2.5">
                      {u.role !== 'ceo' && (
                        <input type="checkbox"
                          checked={selected.has(u._id)}
                          onChange={() => toggleOne(u._id)}
                          className="rounded cursor-pointer"
                          data-testid={`select-user-${u._id}`} />
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <p className="font-medium" style={{ color: '#1B2A4A' }}>{u.name}</p>
                      {u.must_change_password && <p className="text-[10px]" style={{ color: '#BA7517' }}>Must change password</p>}
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      <p className="text-xs truncate max-w-[200px]" style={{ color: '#6B7280' }}>
                        <Mail size={10} className="inline mr-1 -mt-0.5" style={{ color: '#9CA3AF' }} />{u.email}
                      </p>
                      {u.phone && (
                        <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
                          <Phone size={9} className="inline mr-1 -mt-0.5" />{u.phone}
                        </p>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{
                        backgroundColor: u.role === 'ceo' ? '#F3E8FF' : '#EFF6FF',
                        color: u.role === 'ceo' ? '#7C3AED' : '#3B82F6',
                      }}>{u.role}</span>
                      {u.xiaomi_admin_grant && u.role !== 'xiaomi_admin' && u.role !== 'ceo' && (
                        <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded-full font-bold"
                              title="Has full Xiaomi admin access on top of primary role"
                              style={{ backgroundColor: '#FFE5C2', color: '#9A4B0E' }}
                              data-testid={`user-xiaomi-grant-${u._id}`}>
                          + Xiaomi
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <p className="text-xs" style={{ color: '#1B2A4A' }}>{primaryDiv?.name || (u.role === 'ceo' ? 'All' : '—')}</p>
                      {addDivs.length > 0 && <p className="text-[10px]" style={{ color: '#9CA3AF' }}>+{addDivs.join(', ')}</p>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: statusOpt.bg, color: statusOpt.fg }} data-testid={`user-status-${u._id}`}>
                        {statusOpt.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1" style={{ backgroundColor: lg.bg, color: lg.fg }} data-testid={`user-lastlogin-${u._id}`}>
                        <Clock size={9} />{lg.label}
                      </span>
                      {u.login_count > 0 && <p className="text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>{u.login_count} total login{u.login_count === 1 ? '' : 's'}</p>}
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        onClick={() => u.training?.assigned > 0 && setTrainingFor(u)}
                        disabled={!u.training?.assigned}
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1 hover:shadow-sm disabled:cursor-default"
                        style={{ backgroundColor: tr.bg, color: tr.fg }}
                        data-testid={`user-training-${u._id}`}
                      >
                        {tr.label}
                        {u.training?.assigned > 0 && <ChevronRight size={9} />}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {act.daily_sales_entries > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }} title="Daily sales entries">DS {act.daily_sales_entries}</span>}
                        {act.pipeline_deals_owned > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }} title="Pipeline deals owned">PIPE {act.pipeline_deals_owned}</span>}
                        {act.weekly_updates > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#166534' }} title="Weekly updates">WU {act.weekly_updates}</span>}
                        {act.b2b_revenue_entries > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FCE7F3', color: '#9D174D' }} title="B2B revenue entries">B2B {act.b2b_revenue_entries}</span>}
                        {!act.daily_sales_entries && !act.pipeline_deals_owned && !act.weekly_updates && !act.b2b_revenue_entries && (
                          <span className="text-[10px]" style={{ color: '#9CA3AF' }}>No entries yet</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button onClick={() => setEditing(u)} className="p-1 rounded hover:bg-gray-100" title="Edit" data-testid={`edit-user-${u._id}`}><Pencil size={12} style={{ color: '#2A7B88' }} /></button>
                      <button onClick={() => handleResetPassword(u)} className="p-1 rounded hover:bg-gray-100 ml-1" title="Reset password"><RotateCw size={12} style={{ color: '#BA7517' }} /></button>
                      {u.role !== 'ceo' && <button onClick={() => handleDelete(u)} className="p-1 rounded hover:bg-gray-100 ml-1" title="Delete"><Trash2 size={12} style={{ color: '#D4726A' }} /></button>}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-sm" style={{ color: '#9CA3AF' }}>No users match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || creating) && (
        <UserEditModal
          user={editing}
          creating={creating}
          divisions={divOptions}
          hrMode={hrMode}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); onRefresh(); }}
        />
      )}

      {trainingFor && (
        <UserTrainingModal user={trainingFor} onClose={() => setTrainingFor(null)} />
      )}
    </div>
  );
}

function AdoptionPill({ label, value, accent, warning }) {
  return (
    <div className="rounded-md border px-3 py-2" style={{ borderColor: warning ? (accent || '#E5E1DC') : '#E5E1DC', backgroundColor: warning ? (accent || '#1B2A4A') + '10' : '#fff' }}>
      <p className="text-[10px] uppercase tracking-wide" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-lg font-bold flex items-center gap-1" style={{ color: accent || '#1B2A4A' }}>
        {warning && <AlertTriangle size={13} />}
        {value}
      </p>
    </div>
  );
}

function BulkDropdown({ label, children, disabled, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className="text-xs font-medium px-3 py-1.5 rounded-md border hover:shadow-sm disabled:opacity-50"
        style={{ borderColor: '#E5E1DC', backgroundColor: '#fff', color: '#1B2A4A' }}
        data-testid={testId}
      >
        {label} ▾
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute z-20 top-full mt-1 left-0 min-w-[180px] bg-white border rounded-md shadow-lg py-1" style={{ borderColor: '#E5E1DC' }} onClick={() => setOpen(false)}>
            {children}
          </div>
        </>
      )}
    </div>
  );
}

function UserEditModal({ user, creating, divisions, onClose, onSaved, hrMode = false }) {
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    role: user?.role || 'leader',
    division_id: user?.division_id || (hrMode ? (divisions[0]?.id || '') : ''),
    additional_division_ids: user?.additional_division_ids || [],
    status: user?.status || 'active',
    site: user?.site || '',
    // 11-May-2026 · scope-restricted access (Rekha · HR pattern).
    category: user?.category || '',
    scopes: Array.isArray(user?.scopes) ? user.scopes : [],
    allowed_person_ids: Array.isArray(user?.allowed_person_ids) ? user.allowed_person_ids : [],
    // 1-Jun-2026 · Additive Xiaomi admin power.  Lets a non-Xiaomi
    // user (e.g. a CC-Ops or ME leader) ALSO be a full Xiaomi admin
    // without changing their primary role.  See services/xiaomi_access.py.
    xiaomi_admin_grant: !!user?.xiaomi_admin_grant,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [people, setPeople] = useState([]);
  const [peopleSearch, setPeopleSearch] = useState('');

  // Load people once for the manpower-mapping picker.
  useEffect(() => {
    api.get('/people?limit=500').then(r => {
      const list = r?.data?.items || r?.data || [];
      setPeople(Array.isArray(list) ? list : []);
    }).catch(() => setPeople([]));
  }, []);

  const onCategoryChange = (newCat) => {
    setForm(p => {
      const cat = CATEGORY_OPTIONS.find(c => c.value === newCat);
      const next = { ...p, category: newCat };
      // Pre-fill recommended scopes (only if user hasn't customised).
      if (cat && Array.isArray(cat.scopes)) next.scopes = [...cat.scopes];
      return next;
    });
  };

  const toggleScope = (token) => {
    setForm(p => {
      const has = p.scopes.includes(token);
      return { ...p, scopes: has ? p.scopes.filter(s => s !== token) : [...p.scopes, token] };
    });
  };

  const togglePerson = (personId) => {
    setForm(p => {
      const has = p.allowed_person_ids.includes(personId);
      return { ...p, allowed_person_ids: has ? p.allowed_person_ids.filter(x => x !== personId) : [...p.allowed_person_ids, personId] };
    });
  };

  const toggleAddDiv = (id) => {
    setForm(p => {
      const list = p.additional_division_ids.includes(id)
        ? p.additional_division_ids.filter(x => x !== id)
        : [...p.additional_division_ids, id];
      return { ...p, additional_division_ids: list };
    });
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr('');
    try {
      if (creating) await api.post('/admin/users', form);
      else await api.put(`/admin/users/${user._id}`, form);
      onSaved();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-edit-modal">
      <form onSubmit={submit} className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>{creating ? 'Add New User' : `Edit ${user.name}`}</h3>
            {!creating && <p className="text-[11px]" style={{ color: '#6B7280' }}>Last login {timeAgo(user.last_login_at)} · {user.login_count || 0} total logins · Created {user.created_at ? new Date(user.created_at).toLocaleDateString('en-IN') : '—'}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={16} style={{ color: '#6B7280' }} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name" required>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="edit-name" />
            </Field>
            <Field label="Email" required>
              <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="edit-email" />
            </Field>
            <Field label="Phone">
              <input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+91-90000-00000" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="edit-phone" />
            </Field>
            <Field label="Role">
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="edit-role">
                {ROLE_OPTIONS.filter(r => !hrMode || r.value !== 'ceo').map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            <Field label="Primary division">
              <select value={form.division_id} disabled={hrMode} onChange={e => setForm(p => ({ ...p, division_id: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="edit-division">
                {!hrMode && <option value="">None (CEO / Global)</option>}
                {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} data-testid="edit-status">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
          </div>

          <div>
            <label className="block text-[10px] font-medium mb-2" style={{ color: '#6B7280' }}>
              Additional divisions (cross-scope access)
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="edit-additional-divisions">
              {divisions.filter(d => d.id !== form.division_id).map(d => {
                const on = form.additional_division_ids.includes(d.id);
                return (
                  <button type="button" key={d.id} onClick={() => toggleAddDiv(d.id)}
                    className="text-[11px] px-2.5 py-1 rounded-full border font-medium flex items-center gap-1"
                    style={{
                      borderColor: on ? '#1B2A4A' : '#E5E1DC',
                      backgroundColor: on ? '#1B2A4A' : '#fff',
                      color: on ? '#fff' : '#6B7280',
                    }}
                    data-testid={`toggle-add-div-${d.id}`}
                  >
                    {on && <Check size={10} />} {d.name}
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] mt-1.5" style={{ color: '#9CA3AF' }}>
              Example: Nisha = primary "Club Concierge" + additional "Club Social" so she can drive both.
            </p>
          </div>

          {/* ───────── Xiaomi admin grant (1-Jun-2026) ───────── */}
          {form.role !== 'xiaomi_admin' && form.role !== 'ceo' && (
            <div className="border rounded-md p-3" style={{ borderColor: '#FFD7A5', backgroundColor: '#FFF8EE' }}>
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!form.xiaomi_admin_grant}
                  onChange={(e) => setForm(p => ({ ...p, xiaomi_admin_grant: e.target.checked }))}
                  className="mt-0.5"
                  data-testid="edit-xiaomi-admin-grant"
                />
                <div className="flex-1">
                  <div className="text-[12px] font-semibold flex items-center gap-1.5" style={{ color: '#9A4B0E' }}>
                    <Shield size={11} /> Full Xiaomi admin access
                  </div>
                  <p className="text-[10px] mt-0.5" style={{ color: '#6B7280' }}>
                    Adds full <code>xiaomi_admin</code> write power (patch master, import templates, manage benefits, etc.)
                    on top of this user's existing role — their primary division access stays intact.
                    "xiaomi" is auto-added to additional divisions when enabled.
                  </p>
                </div>
              </label>
            </div>
          )}

          <Field label="Site / Desk (optional)">
            <input value={form.site} onChange={e => setForm(p => ({ ...p, site: e.target.value }))} placeholder="e.g. Bengaluru HQ, Marketing" className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }} />
          </Field>

          {/* ───────── Scope-restricted access (11-May-2026) ───────── */}
          <details className="border rounded-md p-3 mt-2" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF8' }}
            open={form.category || form.scopes.length > 0}
            data-testid="edit-scope-section">
            <summary className="cursor-pointer text-[11px] font-semibold flex items-center gap-2 select-none" style={{ color: '#1B2A4A' }}>
              <Shield size={11} style={{ color: '#7A5C2B' }} />
              Scope-restricted access (advanced)
              <span className="text-[10px] font-normal" style={{ color: '#9CA3AF' }}>
                Category · Tabs visible · Allowed people
              </span>
              {form.scopes.length > 0 && (
                <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded font-mono" style={{ background: '#A16207', color: '#fff' }}>
                  {form.scopes.length} scope{form.scopes.length === 1 ? '' : 's'}
                </span>
              )}
            </summary>

            <div className="mt-3 space-y-3">
              <Field label="Category (drives default scope set)">
                <select value={form.category} onChange={e => onCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-md border text-sm" style={{ borderColor: '#E5E1DC' }}
                  data-testid="edit-category">
                  {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </Field>

              <div>
                <label className="block text-[10px] font-medium mb-1.5" style={{ color: '#6B7280' }}>
                  Sub-topics this user can access
                </label>
                <div className="grid grid-cols-2 gap-1.5" data-testid="edit-scope-chips">
                  {SCOPE_OPTIONS.map(s => {
                    const on = form.scopes.includes(s.value);
                    return (
                      <button type="button" key={s.value} onClick={() => toggleScope(s.value)}
                        className="text-[11px] px-2.5 py-1.5 rounded-full border font-medium flex items-center gap-1"
                        style={{
                          borderColor: on ? '#A16207' : '#E5E1DC',
                          backgroundColor: on ? '#A16207' : '#fff',
                          color: on ? '#fff' : '#6B7280',
                        }}
                        data-testid={`toggle-scope-${s.value}`}>
                        {on && <Check size={10} />} {s.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[10px] mt-1.5" style={{ color: '#9CA3AF' }}>
                  Empty scopes = full role-based access. Tick any scope to restrict the user to only the ticked tabs.
                </p>
              </div>

              <div>
                <label className="block text-[10px] font-medium mb-1.5" style={{ color: '#6B7280' }}>
                  Manpower mapping — specific people this user can see ({form.allowed_person_ids.length} selected)
                </label>
                <input value={peopleSearch} onChange={e => setPeopleSearch(e.target.value)}
                  placeholder="Search people by name…"
                  className="w-full px-3 py-2 rounded-md border text-sm mb-2" style={{ borderColor: '#E5E1DC' }}
                  data-testid="edit-people-search" />
                <div className="max-h-40 overflow-y-auto border rounded-md p-2 bg-white" style={{ borderColor: '#E5E1DC' }}
                  data-testid="edit-people-picker">
                  {people
                    .filter(p => {
                      if (!peopleSearch) return true;
                      return (p.name || '').toLowerCase().includes(peopleSearch.toLowerCase());
                    })
                    .slice(0, 50)
                    .map(p => {
                      const on = form.allowed_person_ids.includes(p.id);
                      return (
                        <label key={p.id} className="flex items-center gap-2 py-1 text-[12px] hover:bg-gray-50 px-1 rounded cursor-pointer">
                          <input type="checkbox" checked={on} onChange={() => togglePerson(p.id)} data-testid={`person-${p.id}`} />
                          <span style={{ color: '#1B2A4A' }}>{p.name}</span>
                          <span style={{ color: '#9CA3AF' }}>· {p.division_id || '—'}</span>
                        </label>
                      );
                    })}
                  {people.length === 0 && <p className="text-[11px]" style={{ color: '#9CA3AF' }}>Loading people…</p>}
                </div>
                <p className="text-[10px] mt-1.5" style={{ color: '#9CA3AF' }}>
                  Leave empty = user sees everyone in their division. Pick specific people to limit them further (e.g. Smith, Celeste, Xin · NOT Amma).
                </p>
              </div>
            </div>
          </details>

          {err && <p className="text-xs" style={{ color: '#991B1B' }}>{err}</p>}
        </div>

        <div className="px-5 py-3 border-t flex items-center justify-between gap-2" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAF9F7' }}>
          {!creating && (
            <button type="button" onClick={() => api.post(`/admin/users/${user._id}/reset-password`).then(() => alert('Password reset to Welcome@2026'))} className="text-[11px] underline" style={{ color: '#BA7517' }} data-testid="modal-reset-password">
              Reset password to Welcome@2026
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm border" style={{ borderColor: '#E5E1DC' }}>Cancel</button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: '#1B2A4A' }} data-testid="modal-save">
              {saving ? 'Saving…' : (creating ? 'Create User' : 'Save Changes')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-[10px] font-medium mb-1" style={{ color: '#6B7280' }}>
        {label}{required && <span style={{ color: '#D4726A' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function UserTrainingModal({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useMemo(() => {
    setLoading(true);
    api.get(`/admin/users/${user._id}/training`).then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [user._id]);

  const s = data?.summary || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="user-training-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#E5E1DC' }}>
          <div>
            <h3 className="text-base font-bold" style={{ color: '#1B2A4A' }}>{user.name}'s Training</h3>
            <p className="text-[11px]" style={{ color: '#6B7280' }}>
              {loading ? 'Loading…' : (data?.person_linked === false
                ? 'User not linked to a people record — no SOPs assigned.'
                : `${s.completed || 0} of ${s.total || 0} acknowledged (${s.completion_pct ?? 0}%) · ${s.overdue || 0} overdue`)}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={16} style={{ color: '#6B7280' }} /></button>
        </div>
        <div className="p-5">
          {loading && <p className="text-xs" style={{ color: '#9CA3AF' }}>Loading…</p>}
          {!loading && data?.person_linked && (
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: '#F7F5F2' }}>
                <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>SOP</th>
                <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Category</th>
                <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Age</th>
                <th className="text-left px-3 py-2 text-xs font-medium" style={{ color: '#6B7280' }}>Status</th>
              </tr></thead>
              <tbody className="divide-y" style={{ borderColor: '#F3F4F6' }}>
                {(data.assigned || []).slice(0, 50).map(r => (
                  <tr key={r.sop_id}>
                    <td className="px-3 py-2 text-xs" style={{ color: '#1B2A4A' }}>{r.title}</td>
                    <td className="px-3 py-2 text-[11px]" style={{ color: '#6B7280' }}>{r.category}</td>
                    <td className="px-3 py-2 text-[11px]" style={{ color: '#6B7280' }}>{r.age_days != null ? `${r.age_days}d` : '—'}</td>
                    <td className="px-3 py-2">
                      {r.acknowledged ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#DCFCE7', color: '#166534' }}><CheckCircle2 size={9} className="inline -mt-0.5 mr-1" />Completed</span>
                      ) : r.overdue ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}><AlertTriangle size={9} className="inline -mt-0.5 mr-1" />Overdue</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
                {(data.assigned || []).length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-sm" style={{ color: '#9CA3AF' }}>No SOPs assigned yet.</td></tr>}
              </tbody>
            </table>
          )}
          {!loading && data?.person_linked === false && (
            <p className="text-xs" style={{ color: '#9CA3AF' }}>To link this user to a people record, open People → Add and match by email. Once linked, assigned SOPs will appear here.</p>
          )}
        </div>
      </div>
    </div>
  );
}
